import { useAppForm } from '@sparklayer-io/spark-dash-core/lib/app-form';
import { ApiResponseError } from '@sparklayer-io/spark-dash-core/types/errors';
import { Alert } from '@ui/themed';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import type { I18nPrefix } from '../../../i18n/prefix';
import type { DashboardPageProps } from '../../../lib/dashboard-page';
import { AuthButton } from '../components/auth-button';
import { AuthLayout } from '../components/auth-layout';
import { AuthLogo } from '../components/auth-logo';
import { FormErrorAlert } from '../components/form-error-alert';
import { ReturnToLoginLink } from '../components/return-to-login-link';
import { TextInput } from '../components/text-input';
import type { AuthFormError } from '../types';

const PAGE = 'features:auth.pages.forgotten_password_page' satisfies I18nPrefix;

// Matches the spark-dash-api password broker throttle, which reports a throttled request as sent.
const RESEND_COOLDOWN_SECONDS = 60;

export function ForgottenPasswordPage({ handlers }: DashboardPageProps) {
  const { apiHandler, navigationHandler } = handlers;
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<AuthFormError | null>(null);
  const [resendAvailableAt, setResendAvailableAt] = useState<number>(0);
  const [now, setNow] = useState<number>(0);
  const resendCooldownSeconds = Math.max(0, Math.ceil((resendAvailableAt - now) / 1000));
  const isResendBlocked = resendCooldownSeconds > 0;

  useEffect(() => {
    if (!isResendBlocked) return;
    const intervalId = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(intervalId);
  }, [isResendBlocked]);

  const form = useAppForm({
    defaultValues: { email: '' },
    onSubmit: async ({ value }) => {
      setError(null);
      setSuccess(null);
      setLoading(true);
      try {
        await apiHandler.fetch({ method: 'GET', path: '/sanctum/csrf-cookie', maxRedirects: 0 });
        const data = await apiHandler.fetch<{ message: string }>({
          method: 'POST',
          path: '/forgot-password',
          payload: { email: value.email },
        });
        setSuccess(data.message);
        const sentAt = Date.now();
        setNow(sentAt);
        setResendAvailableAt(sentAt + RESEND_COOLDOWN_SECONDS * 1000);
      } catch (err) {
        if (err instanceof ApiResponseError && err.status === 422) {
          setError(err.data as { errors: Record<string, string[]> });
        } else if (err instanceof ApiResponseError && err.status === 429) {
          setError({ message: t(`${PAGE}.rate_limited_error`) });
        } else {
          setError({ message: t('errors:generic') });
        }
      }
      setLoading(false);
    },
  });

  return (
    <AuthLayout>
      <div className="sm:mx-auto sm:w-full sm:max-w-lg">
        <AuthLogo />
        <div className="mt-8 rounded-lg border border-spark-border-subtle bg-spark-surface p-10 shadow-xl sm:p-14">
          <div>
            <h2 className="mb-4 font-bold text-spark-text-primary text-xl sm:text-2xl">
              {t(`${PAGE}.heading`)}
            </h2>
            <p className="max-w mt-2 text-sm text-spark-text-secondary">
              {t(`${PAGE}.description`)}
            </p>
          </div>
          <div className="mt-6">
            <form
              className="space-y-6"
              onSubmit={(event) => {
                event.preventDefault();
                void form.handleSubmit();
              }}
            >
              {success && (
                <Alert variant="success" headingLevel="h3">
                  <p className="text-sm">{success}</p>
                </Alert>
              )}
              {error && <FormErrorAlert error={error} />}

              <form.Field name="email">
                {(field) => (
                  <TextInput
                    name="email"
                    type="email"
                    label={t(`${PAGE}.email_label`)}
                    autoComplete="email"
                    required
                    placeholder={t(`${PAGE}.email_placeholder`)}
                    value={field.state.value}
                    onChange={field.handleChange}
                  />
                )}
              </form.Field>

              <div>
                <AuthButton loading={loading} disabled={isResendBlocked}>
                  {t(`${PAGE}.submit`)}
                </AuthButton>
                {isResendBlocked && (
                  <p className="mt-3 text-center text-sm text-spark-text-secondary">
                    {t(`${PAGE}.resend_cooldown`, { seconds: resendCooldownSeconds })}
                  </p>
                )}
              </div>
            </form>
          </div>
        </div>
        <ReturnToLoginLink
          navigationHandler={navigationHandler}
          className="max-w pt-8 text-sm text-spark-text-secondary"
        />
      </div>
    </AuthLayout>
  );
}
